import { makeApiRequest } from './api';

export interface IBusiness {
  id: string;
  tenantId: string;
  userId: string;
  name: string;
  /** Business type **code** from `business_types`. */
  type: string;
  email: string;
  /** Industry **code** from `industries`. */
  industryType: string;
  website?: string;
  phoneNumber?: string;
  businessDescription?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  /** Band codes from `reference_bands`, not numbers. */
  numberOfStores?: string;
  annualRevenue?: string;
  currentAnnualAdSpend?: string;
  eCommercePlatform?: string;
  /** Consumer group code from `consumer_groups`. */
  primaryAudience?: string;
  averageOrderValue?: string;
  legalBusinessName?: string;
  taxId?: string;
  businessLegalDocument?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IBusinessOfflineLocation {
  id: string;
  businessId: string;
  storeName?: string;
  geoLocation?: {
    longitude: number;
    latitude: number;
  };
  address: string;
  postalCode: number;
}

export type CreateBusinessOfflineLocationPayload = {
  storeName?: string;
  geoLocation?: {
    longitude: number;
    latitude: number;
  };
  address: string;
  postalCode: number;
};

/**
 * What onboarding sends. Required: name, type, industryType, country and the
 * logo. Everything else is optional — the marketing profile and legal details
 * are edited from Settings and demanded by the pre-launch checklist instead.
 *
 * `type` and `industryType` are reference **codes** from `GET /reference-data`,
 * never display labels. Sending a label used to store a different value in the
 * database depending on the UI language.
 */
export interface CreateBusinessPayload {
  logo?: File;
  name: string;
  type: string;
  industryType: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
  /** Omit to inherit the address the user verified at login. */
  email?: string;
  phoneNumber?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  /** Persisted onto the user, driving UI and outbound email locale. */
  preferredLanguage?: string;
}

/** Settings-page edits: the marketing profile and the legal details. */
export interface UpdateBusinessPayload {
  name?: string;
  type?: string;
  industryType?: string;
  country?: string;
  email?: string;
  phoneNumber?: string;
  website?: string;
  businessDescription?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;

  // Marketing profile — band codes from `GET /reference-data`.
  numberOfStores?: string;
  annualRevenue?: string;
  currentAnnualAdSpend?: string;
  averageOrderValue?: string;
  eCommercePlatform?: string;
  /** Consumer group code. */
  primaryAudience?: string;

  // Legal
  legalBusinessName?: string;
  taxId?: string;
  businessLegalDocument?: string;
}

export interface CreateBusinessResponse {
  data: IBusiness;
  success: boolean;
}

export interface GetBusinessesResponse {
  message: string;
  success: boolean;
  data: {
    businesses: IBusiness[];
  };
}

export const createBusinessService = async (
  payload: CreateBusinessPayload,
  token?: string
): Promise<CreateBusinessResponse | null> => {
  const formData = new FormData();

  // Append logo file if present
  if (payload.logo) {
    formData.append('logo', payload.logo);
  }

  // Append other fields
  Object.entries(payload).forEach(([key, value]) => {
    if (key !== 'logo' && value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });

  const response = await makeApiRequest<CreateBusinessResponse>('/businesses', {
    method: 'POST',
    body: formData,
    token,
  });
  return response;
};

export const getUserBusinessesService = async (
  token?: string
): Promise<GetBusinessesResponse | null> => {
  const response = await makeApiRequest<GetBusinessesResponse>(
    '/businesses/user/me',
    {
      method: 'GET',
      token,
    }
  );
  return response;
};

export interface GetBusinessByIdResponse {
  message: string;
  success: boolean;
  data: IBusiness;
}

export const getBusinessByIdService = async (
  businessId: string,
  token?: string
): Promise<IBusiness | null> => {
  const response = await makeApiRequest<GetBusinessByIdResponse>(
    `/businesses/${businessId}`,
    {
      method: 'GET',
      token,
    }
  );
  return response?.data || null;
};
export const updateBusinessService = async (
  businessId: string,
  payload: Partial<IBusiness>,
  token?: string
): Promise<IBusiness | null> => {
  const response = await makeApiRequest<IBusiness>(
    `/businesses/${businessId}`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
      token,
    }
  );
  return response;
};

type OfflineLocationsResponse =
  | IBusinessOfflineLocation[]
  | {
      data?: IBusinessOfflineLocation[] | IBusinessOfflineLocation;
      offlineLocations?: IBusinessOfflineLocation[];
      location?: IBusinessOfflineLocation;
    };

const unwrapOfflineLocations = (
  response: OfflineLocationsResponse | null
): IBusinessOfflineLocation[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.offlineLocations))
    return response.offlineLocations;
  return [];
};

const unwrapOfflineLocation = (
  response: OfflineLocationsResponse | null
): IBusinessOfflineLocation | null => {
  if (!response || Array.isArray(response)) return null;
  if (response.data && !Array.isArray(response.data)) return response.data;
  return response.location ?? null;
};

export const getBusinessOfflineLocationsService = async (
  businessId: string,
  token?: string
): Promise<IBusinessOfflineLocation[]> => {
  const response = await makeApiRequest<OfflineLocationsResponse>(
    `/businesses/${businessId}/offline-locations`,
    {
      method: 'GET',
      token,
    }
  );

  return unwrapOfflineLocations(response);
};

export const createBusinessOfflineLocationService = async (
  businessId: string,
  payload: CreateBusinessOfflineLocationPayload,
  token?: string
): Promise<IBusinessOfflineLocation | null> => {
  const response = await makeApiRequest<OfflineLocationsResponse>(
    `/businesses/${businessId}/offline-locations`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      token,
    }
  );

  return unwrapOfflineLocation(response);
};

export const deleteBusinessOfflineLocationService = async (
  businessId: string,
  locationId: string,
  token?: string
): Promise<void> => {
  await makeApiRequest(
    `/businesses/${businessId}/offline-locations/${locationId}`,
    {
      method: 'DELETE',
      token,
    }
  );
};
